// The outliner (OutlinerView on the Mac): the selection's subtree as a table — the title, one line of synopsis, words,
// then the project's fields — every field an inline editor (FieldEditor, compact: option and cast menus, numbers and
// text that save on Return or on leaving, switches; dates read-only), writing through `setMetadata`. The Target column
// shows a bar of the words under the row against its target (verdigris once met; a folder counts everything in it).
//
// Several rows at once: click a row, ⌘/Ctrl-click to add one, ⇧-click for a range; the context menu (⇧F10, or touch
// and hold) sets Status, Label, POV or Section type on all of them, a word target on each, or Include in Compile —
// one change per document (`PlanEditor.setMetadataOn`). Click a column heading to sort by it (again to reverse; the
// tree is flattened while sorted); the Group and Sort menu beside the tabs groups by Status (in workflow order), Label
// or POV, goes back to the contents' order, and chooses the columns (remembered in this browser; a phone starts with
// the few that fit). Double-click a title to rename it. Where the project can't be written the cells show values.
// Outside the story's scenes (Research & Notes, Publishing: `planShowsScenes`) the scene columns are left out — point of
// view, section type, setting, story time and duration, beat, a post's fields.
import { storageId } from '../store/storageId.ts';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { isFarFromTarget, type Field, type MetadataValue, type OpenedProject, type Project, type TreeNode } from '@writing-suite/core';
import { isBuiltInView, outlinerFields, planShowsScenes, POSTS_VIEW_ID, storedColumn, webColumn, WordTarget, type GridView } from '@writing-suite/core/plan';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { RenameField } from '../shell/ContentsTree.tsx';
import { followLink } from '../shell/route.ts';
import { structureEdits, useWorkspace } from '../shell/workspace.ts';
import { planEdit, PREF, useSharedPreference } from '../store/plan.ts';
import { beatScenesFor, beatSheetFor } from '../store/planModel.ts';
import { deleteGridView, gridViewsFor, saveGridView, updateGridView } from '../store/fields.ts';
import { FieldsDialog } from './FieldsDialog.tsx';
import { announce } from '../shell/context.ts';
import { groupRows, sortRows, type Grouping, type OutlineRow, type SortKey, type SortValue } from '../store/planModel.ts';
import { choiceEntries, FieldEditor } from './FieldEditor.tsx';
import { flattenTree, formatNumber, hasChildren } from './helpers.ts';
import { isJournalPosts, plannedPost, PostHeader, withoutSections } from './plan/posts.tsx';
import { journalPostsInTree, POST_ONLY_FIELDS } from '@writing-suite/core';
import { TextDialog } from './Margin.tsx';
import { MenuButton, MenuPopup, type MenuEntry } from './Menu.tsx';
import { rangeBetween, wordTargetInput } from './structure.ts';
import { usePhone } from './plan/hooks.ts';
import { TargetBar } from './plan/IndexCard.tsx';

export interface OutlineColumn {
  id: string;
  label: string;
}

/** The outliner's columns after the title: synopsis, words, where the scene starts (with the story's scenes), then
 *  every field. */
export const outlineColumns = (fields: Field[], scenes = true): OutlineColumn[] => [
  { id: 'synopsis', label: 'Synopsis' },
  { id: 'words', label: 'Words' },
  ...(scenes ? [{ id: 'position', label: 'Position' }] : []),
  ...fields.map((f) => ({ id: `field:${f.id}`, label: f.id === 'word-target' ? 'Target' : f.name })),
];

/** Where each manuscript scene starts (0…1) and the book's words, as the beat board reads them. A folder starts where
 *  its first scene does. */
export function scenePositions(project: Project): { starts: Map<string, number>; words: number } {
  const opened = project as OpenedProject;
  if (!opened.record) return { starts: new Map(), words: 0 };
  const scenes = beatScenesFor(opened, beatSheetFor(opened));
  return { starts: new Map(scenes.map((s) => [s.id, s.start])), words: scenes.reduce((n, s) => n + s.words, 0) };
}

const percent = (f: number) => `${Math.round(f * 100)}%`;

/** What a phone shows before the writer chooses (iOS starts with the columns that fit). */
export const PHONE_COLUMNS = ['words', 'field:status'];

/** The hidden columns: the writer's choice, else (on a phone) everything but the few that fit. Outside the story's
 *  scenes, no scene columns at all. */
export function useOutlineColumns(projectId: string, fields: Field[], phone: boolean, scenes = true) {
  const columns = outlineColumns(outlinerFields(fields, scenes), scenes);
  const [hidden, setHidden] = useSharedPreference<string[] | null>(PREF.outlinerHidden(storageId(projectId)), null);
  const effective = hidden ?? (phone ? columns.map((c) => c.id).filter((id) => !PHONE_COLUMNS.includes(id)) : []);
  return { columns, hidden: effective, setHidden };
}

const BULK_FIELDS = ['status', 'label', 'pov', 'section-type'];

export function Outliner({ project, tree, node, hrefFor, live, arrangement, page }: PageProps) {
  const ws = useWorkspace();
  const phone = usePhone();
  const editable = !!ws?.canWrite && !!live;
  const fields = useMemo(() => project.fields(), [project]);
  const entities = useMemo(() => project.entities(), [project]);
  const { columns, hidden } = useOutlineColumns(project.id, fields, phone, planShowsScenes(arrangement.role));
  const [grouping] = useSharedPreference<Grouping>(PREF.outlinerGroup, 'none');
  const [sort, setSort] = useSharedPreference<SortKey | null>(PREF.outlinerSort, null);
  const shown = columns.filter((c) => !hidden.includes(c.id));
  const positions = useMemo(() => (shown.some((c) => c.id === 'position') ? scenePositions(project) : { starts: new Map<string, number>(), words: 0 }), [project, shown]);
  const sheet = useMemo(() => beatSheetFor(project as OpenedProject), [project]);
  /** Where a row starts: its fragment's start, else (a folder) its first scene's. */
  const startOf = (n: TreeNode): number | null => {
    if (n.fragmentId && positions.starts.has(n.fragmentId)) return positions.starts.get(n.fragmentId)!;
    for (const c of n.children) {
      const s = startOf(c);
      if (s !== null) return s;
    }
    return null;
  };
  const heading = node ? node.title || 'Untitled' : ws?.arrangement.role === 'collection' ? ws.arrangement.name : 'Outline';

  const wordsOf = (n: TreeNode) => (hasChildren(n) ? project.subtreeWords(n) : n.fragmentId ? project.words(n.fragmentId) : 0);
  const valueOf = (row: OutlineRow, column: string): SortValue => {
    const n = row.node;
    if (column === 'title') return n.title || 'Untitled';
    if (column === 'words') return wordsOf(n);
    if (column === 'position') return startOf(n) ?? 2;
    const f = n.fragmentId ? project.fragment(n.fragmentId) : null;
    if (!f) return null;
    if (column === 'synopsis') return f.synopsis;
    const id = column.slice('field:'.length);
    const v = f.metadata[id];
    if (!v) return null;
    if (v.type === 'number') return v.value;
    if (v.type === 'date') return v.value.getTime();
    if (v.type === 'boolean') return v.value ? 1 : 0;
    if (id === 'word-target') return WordTarget.valueOf(v);
    return project.displayValue(id, v);
  };

  // A journal's posts: posts and their folders (sections are in a post's plan); a post's plan: its sections.
  const posts = isJournalPosts(project, arrangement);
  const planned = posts ? plannedPost(tree, node?.id ?? null) : null;
  const base = planned ? flattenTree(planned.node.children)
    : posts ? flattenTree(withoutSections(node ? [node] : tree))
    : flattenTree(node ? [node] : tree);
  const sorted = sort ? sortRows(base, sort, (r) => valueOf(r, sort.column)) : base;
  const groupField = grouping === 'none' ? undefined : fields.find((f) => f.id === grouping);
  const groups = groupField
    ? groupRows(sorted, groupField, (id) => project.fragment(id)?.metadata, (v) => project.displayValue(groupField.id, { type: 'text', value: v }) || v)
    : [{ key: null, title: '', rows: sorted }];
  const order = groups.flatMap((g) => g.rows.map((r) => r.node.id));

  // The selection: the page's item, plus ⌘- or ⇧-clicked rows; the anchor is where a ⇧-click's range starts.
  const [picked, setPicked] = useState<string[]>(node ? [node.id] : []);
  const anchor = useRef<string | null>(node?.id ?? null);
  const pickedNow = picked.filter((id) => order.includes(id));
  const [menu, setMenu] = useState<{ x: number; y: number; ids: string[] } | null>(null);
  const [targetFor, setTargetFor] = useState<string[] | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);

  const pick = (e: MouseEvent, id: string) => {
    if ((e.target as HTMLElement).closest('a, button, input, select, textarea, [role="menu"]')) return;
    if (e.metaKey || e.ctrlKey) setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    else if (e.shiftKey) setPicked(rangeBetween(order, anchor.current ?? id, id));
    else setPicked([id]);
    if (!e.shiftKey) anchor.current = id;
  };
  const targets = (id: string) => (pickedNow.includes(id) ? order.filter((x) => pickedNow.includes(x)) : [id]);
  const fragmentsOf = (ids: string[]) => ids.map((id) => base.find((r) => r.node.id === id)?.node.fragmentId).filter((f): f is string => !!f);

  const bulkEntries = (ids: string[]): MenuEntry[] => {
    const fids = fragmentsOf(ids);
    if (!editable || !fids.length || !live) return [{ kind: 'heading', label: 'Read only' }];
    const first = project.fragment(fids[0]);
    const out: MenuEntry[] = [];
    if (ids.length > 1) out.push({ kind: 'heading', label: `${ids.length} items` });
    for (const id of BULK_FIELDS) {
      const field = fields.find((f) => f.id === id);
      if (!field || (field.type === 'enum' && !field.options.length) || (field.type !== 'enum' && field.type !== 'entity-ref')) continue;
      if (out.length) out.push({ kind: 'separator' });
      out.push({ kind: 'heading', label: field.name });
      out.push(...choiceEntries(field, entities, first?.metadata[id], (v) => planEdit(live, (e) => e.setMetadataOn(fids, id, v))));
    }
    out.push(
      { kind: 'separator' },
      { label: 'Set Word Target…', onSelect: () => setTargetFor(fids) },
      { label: 'Include in Compile', onSelect: () => planEdit(live, (e) => e.setIncludeInCompileOn(fids, true)) },
      { label: 'Leave Out of Compile', onSelect: () => planEdit(live, (e) => e.setIncludeInCompileOn(fids, false)) },
    );
    return out;
  };

  const openMenu = (x: number, y: number, id: string) => {
    const ids = targets(id);
    if (!pickedNow.includes(id)) setPicked([id]);
    setMenu({ x, y, ids });
  };

  const sortBy = (column: string) => {
    if (sort?.column === column) setSort({ column, ascending: !sort.ascending });
    else setSort({ column, ascending: true });
  };
  const sortState = (column: string): 'ascending' | 'descending' | undefined =>
    sort?.column === column ? (sort.ascending ? 'ascending' : 'descending') : undefined;
  const flat = !!sort || !!groupField;

  const row = ({ node: n, depth }: OutlineRow) => {
    const fragment = n.fragmentId ? project.fragment(n.fragmentId) : null;
    const folder = hasChildren(n);
    // A journal's post opens its plan; a section, like a scene, opens in Write.
    const post = posts && !planned && journalPostsInTree(tree).some((p) => p.nodeId === n.id);
    const href = folder || post ? hrefFor('plan/outliner', n.id) : hrefFor('write', n.id);
    const isPicked = pickedNow.includes(n.id);
    return (
      <tr
        key={n.id}
        data-node-id={n.id}
        className={`${folder ? 'folder' : ''}${isPicked ? ' picked' : ''}${ws?.filter?.isContext(n.id) ? ' filter-context' : ''}`}
        aria-selected={isPicked}
        onClick={(e) => pick(e, n.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          openMenu(e.clientX, e.clientY, n.id);
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            e.preventDefault();
            openMenu(r.left + 24, r.bottom, n.id);
          } else if (e.key === 'F2' && ws?.canStructure && n.fragmentId && (e.target as HTMLElement).tagName === 'A') {
            e.preventDefault();
            setRenaming(n.id);
          }
        }}
      >
        <th scope="row" className={`title depth-${flat ? 0 : Math.min(depth, 6)}`}>
          {renaming === n.id ? (
            <RenameField
              title={n.title}
              onDone={(title) => {
                if (title !== null && ws) structureEdits(ws).rename(n.id, title);
                setRenaming(null);
              }}
            />
          ) : (
            <a
              href={href}
              onClick={(e) => followLink(e, href)}
              onDoubleClick={(e) => {
                if (!ws?.canStructure || !n.fragmentId) return;
                e.preventDefault();
                setRenaming(n.id);
              }}
            >
              {n.title || 'Untitled'}
            </a>
          )}
        </th>
        {shown.map((c) => {
          if (c.id === 'synopsis') return <td key={c.id} className="synopsis">{fragment?.synopsis ?? ''}</td>;
          if (c.id === 'position') {
            const start = startOf(n);
            if (start === null) return <td key={c.id} className="num" />;
            const beat = fragment?.metadata.beat;
            const target = !folder && beat?.type === 'text' ? sheet?.beats.find((b) => b.id === beat.value)?.target : undefined;
            const far = target !== undefined && isFarFromTarget(start, target, positions.words);
            return (
              <td key={c.id} className={`num position${far ? ' far' : ''}${folder ? ' folder-value' : ''}`} data-column="position"
                title={far ? 'Further from where the beat sheet puts this beat than the structure check allows' : 'Where it starts, by words into the book'}>
                {target !== undefined ? `${percent(start)} · near ${percent(target)}` : percent(start)}
              </td>
            );
          }
          if (c.id === 'words') {
            const words = wordsOf(n);
            return (
              <td key={c.id} className="num">
                {words ? formatNumber(words) : '—'}
              </td>
            );
          }
          const fieldId = c.id.slice('field:'.length);
          const field = fields.find((f) => f.id === fieldId)!;
          // A post's section has no Status, number or publish date of its own.
          if (!fragment || (planned && POST_ONLY_FIELDS.has(fieldId))) return <td key={c.id} data-field-id={fieldId} />;
          const target = fieldId === 'word-target' ? WordTarget.value(fragment.metadata) : null;
          const bar = target !== null ? <TargetBar words={wordsOf(n)} target={target} className="target-bar cell-bar" /> : null;
          if (!editable) {
            return (
              <td key={c.id} data-field-id={fieldId}>
                {project.displayValue(fieldId, fragment.metadata[fieldId])}
                {bar}
              </td>
            );
          }
          return (
            <td key={c.id} className="cell-editor" data-field-id={fieldId}>
              <FieldEditor
                inline
                emptyText={fieldId === 'beat' && !folder && sheet ? 'No beat' : undefined}
                field={field}
                value={fragment.metadata[fieldId]}
                project={project}
                entities={entities}
                onChange={(value: MetadataValue | null) => live!.edit((e) => e.setMetadata(fragment.id, fieldId, value))}
              />
              {bar}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="outline-page" data-grouping={grouping} data-sorted={sort ? `${sort.column}:${sort.ascending ? 'asc' : 'desc'}` : undefined}>
      {planned ? <PostHeader project={project} tree={tree} hrefFor={hrefFor} live={live} page={page} post={planned.post} node={planned.node} /> : (
        <header className="page-head">
          <h1 tabIndex={-1}>{heading}</h1>
          <p className="page-sub">
            {base.length === 1 ? '1 item' : `${base.length} items`}
            {pickedNow.length > 1 ? ` · ${pickedNow.length} selected` : ''}
          </p>
        </header>
      )}
      <div className="table-wrap" role="region" aria-label={`Outline of ${heading}`} tabIndex={0}>
        <table className="outline" aria-multiselectable="true">
          <thead>
            <tr>
              <th scope="col" aria-sort={sortState('title')}>
                <button type="button" className="sort-button" onClick={() => sortBy('title')}>
                  Title
                </button>
              </th>
              {shown.map((c) => (
                <th key={c.id} scope="col" className={c.id === 'words' ? 'num' : undefined} aria-sort={sortState(c.id)} data-column={c.id}>
                  <button type="button" className="sort-button" onClick={() => sortBy(c.id)}>
                    {c.label}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g.key ?? '\u0000none'} data-group={groupField ? (g.key ?? '') : undefined}>
              {groupField ? (
                <tr className="group-row">
                  <th scope="rowgroup" colSpan={shown.length + 1}>
                    {g.title} <span className="group-count">{g.rows.length}</span>
                  </th>
                </tr>
              ) : null}
              {g.rows.map(row)}
            </tbody>
          ))}
        </table>
      </div>
      {menu ? <MenuPopup label="Outline rows" at={menu} entries={bulkEntries(menu.ids)} onClose={() => setMenu(null)} /> : null}
      {targetFor && live ? (
        <TextDialog
          title={targetFor.length === 1 ? 'Word Target' : `Word Target for ${targetFor.length} Documents`}
          label="Words"
          confirm="Set"
          message={<p>Each document gets this target (a folder’s counts everything in it). Leave it at 0 to remove the targets.</p>}
          initial=""
          onCancel={() => setTargetFor(null)}
          onDone={(text) => {
            const ids = targetFor;
            setTargetFor(null);
            planEdit(live, (e) => e.setMetadataOn(ids, 'word-target', wordTargetInput(text)));
          }}
        />
      ) : null}
    </div>
  );
}

/** Beside the tabs: the View menu (a named set of columns with its grouping and sort: the built-in ones and the
 *  project's own) and Group and Sort (Group By, Shelf Order, the columns, Edit Fields…). */
export function OutlinerControls({ project, arrangement, live }: PageProps) {
  const ws = useWorkspace();
  const phone = usePhone();
  const fields = project.fields();
  const scenes = planShowsScenes(arrangement.role);
  const { columns, hidden, setHidden } = useOutlineColumns(project.id, fields, phone, scenes);
  const [grouping, setGrouping] = useSharedPreference<Grouping>(PREF.outlinerGroup, 'none');
  const [sort, setSort] = useSharedPreference<SortKey | null>(PREF.outlinerSort, null);
  const [viewId, setViewId] = useSharedPreference<string | null>(PREF.outlinerView(storageId(project.id)), null);
  const [editingFields, setEditingFields] = useState(false);
  const [naming, setNaming] = useState<'save' | 'rename' | null>(null);
  const writable = !!ws?.canWrite && !!live;
  const groupings: { id: Grouping; name: string }[] = [
    { id: 'none', name: 'None' }, { id: 'status', name: 'Status' }, { id: 'label', name: 'Label' }, { id: 'pov', name: 'POV' },
  ];

  // Views speak the Mac outliner's column ids; this table's are mapped to and from them.
  const journal = isJournalPosts(project, arrangement);
  const views = gridViewsFor(project, scenes, journal);
  const current = views.find((v) => v.id === viewId) ?? null;
  const shownColumns = columns.filter((c) => !hidden.includes(c.id)).map((c) => c.id);
  const viewColumns = (v: GridView) => v.columns.map(webColumn).filter((c): c is string => !!c && columns.some((x) => x.id === c));
  const viewSort = (v: GridView): SortKey | null => {
    const column = v.sort ? (v.sort.column === 'title' ? 'title' : webColumn(v.sort.column)) : null;
    return column && v.sort ? { column, ascending: v.sort.ascending } : null;
  };
  const sameSort = (a: SortKey | null, b: SortKey | null) => (a?.column ?? null) === (b?.column ?? null) && (a?.ascending ?? true) === (b?.ascending ?? true);
  const edited = !!current && (
    [...new Set(viewColumns(current))].sort().join() !== [...shownColumns].sort().join() || current.group !== grouping || !sameSort(viewSort(current), sort)
  );
  const apply = (v: GridView) => {
    const wanted = new Set(viewColumns(v));
    setHidden(columns.filter((c) => !wanted.has(c.id)).map((c) => c.id));
    setGrouping(groupings.some((g) => g.id === v.group) ? (v.group as Grouping) : 'none');
    setSort(viewSort(v));
    setViewId(v.id);
    announce(`Showing ${v.name}`);
  };
  // A journal's outliner starts with its Posts view, once per project in this browser.
  useEffect(() => {
    if (!journal || viewId !== null) return;
    const key = `ws:outlinerPostsView:${storageId(project.id)}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      return;
    }
    const posts = views.find((v) => v.id === POSTS_VIEW_ID);
    if (posts) apply(posts);
  }, [journal, project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const asStored = () => ({
    columns: shownColumns.map(storedColumn), group: grouping, sort: sort ? { column: sort.column === 'title' ? 'title' : storedColumn(sort.column), ascending: sort.ascending } : null,
  });

  const viewEntries: MenuEntry[] = [
    ...views.map((v): MenuEntry => ({ kind: 'radio', label: v.name, checked: v.id === viewId, onSelect: () => apply(v) })),
    ...(writable ? [
      { kind: 'separator' } as MenuEntry,
      { label: 'Save Current Columns as View…', onSelect: () => setNaming('save') },
      ...(current && !isBuiltInView(current) ? [
        { label: `Update “${current.name}”`, disabled: !edited, onSelect: () => { updateGridView(live!, current.id, asStored()); announce(`Updated ${current.name}`); } },
        { label: `Rename “${current.name}”…`, onSelect: () => setNaming('rename') },
        { label: `Delete “${current.name}”`, onSelect: () => { deleteGridView(live!, current); setViewId(null); announce(`Deleted ${current.name}`); } },
      ] as MenuEntry[] : []),
    ] : []),
  ];

  const entries: MenuEntry[] = [
    { kind: 'heading', label: 'Group By' },
    ...groupings.filter((g) => g.id === 'none' || fields.some((f) => f.id === g.id)).map((g): MenuEntry => ({
      kind: 'radio', label: g.name, checked: grouping === g.id, onSelect: () => setGrouping(g.id),
    })),
    { kind: 'separator' },
    { label: 'Shelf Order', checked: !sort, disabled: !sort, onSelect: () => setSort(null) },
    { kind: 'separator' },
    { kind: 'heading', label: 'Columns' },
    ...columns.map((c): MenuEntry => ({
      kind: 'check',
      label: c.label,
      checked: !hidden.includes(c.id),
      onSelect: () => setHidden(hidden.includes(c.id) ? hidden.filter((h) => h !== c.id) : [...hidden, c.id]),
    })),
    ...(live ? [{ kind: 'separator' } as MenuEntry, { label: 'Edit Fields…', onSelect: () => setEditingFields(true) }] : []),
  ];
  const name = groupings.find((g) => g.id === grouping)?.name;
  return (
    <div className="plan-controls">
      <MenuButton label="View" className="quiet-button header-menu" entries={viewEntries}>
        {current ? (edited ? `${current.name} (edited)` : current.name) : 'View'}
      </MenuButton>
      <MenuButton label="Group and Sort" className="quiet-button header-menu" entries={entries}>
        {grouping === 'none' ? 'Group and Sort' : `Grouped by ${name}`}
      </MenuButton>
      {editingFields && live ? <FieldsDialog project={project} live={live} write={writable} onClose={() => setEditingFields(false)} /> : null}
      {naming && live ? (
        <TextDialog
          title={naming === 'save' ? 'Save View' : 'Rename View'}
          label="Name"
          confirm={naming === 'save' ? 'Save' : 'Rename'}
          message={naming === 'save' ? <p>Keeps the columns shown, the grouping and the sort under a name, for every device the project is on.</p> : undefined}
          initial={naming === 'rename' && current ? current.name : ''}
          onCancel={() => setNaming(null)}
          onDone={(text) => {
            const mode = naming;
            setNaming(null);
            if (mode === 'save') {
              const { view, error } = saveGridView(live, { name: text, ...asStored() });
              if (view) {
                setViewId(view.id);
                announce(`Saved ${view.name}`);
              } else if (error) announce(error);
            } else if (current) {
              const error = updateGridView(live, current.id, { name: text });
              announce(error ?? `Renamed to ${text.trim()}`);
            }
          }}
        />
      ) : null}
    </div>
  );
}
